import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {APIStatus} from "../../types/APITypes"
import {BroadcastPreviewAlert} from "../../types/view/BroadcastPreviewView"
import {BroadcastPreviewContent} from "../../types/view/BroadcastPreviewView"

interface SliceState {
    fetchContentAPIStatus: APIStatus
    fetchAlertAPIStatus: APIStatus

    needUpdateState: boolean
    needReFetch: boolean
    needUpdateAlertInterval: boolean

    content: BroadcastPreviewContent
    alert?: BroadcastPreviewAlert
}

const initialState = {
    fetchContentAPIStatus: APIStatus.Initial,
    fetchAlertAPIStatus: APIStatus.Initial,

    needUpdateState: false,
    needUpdateAlertInterval: false,
    needReFetch: false,
} as SliceState

const EMPTY_CONTENT_ID = 0

export const broadcastPreviewSlice = createSlice({
    name: "broadcastPreview",
    initialState,
    reducers: {
        startContentFetch(state: SliceState) {
            state.fetchContentAPIStatus = APIStatus.Loading
        },
        failContentFetch(state: SliceState) {
            state.fetchContentAPIStatus = APIStatus.Failure
        },
        successContentFetch(
            state: SliceState,
            action: PayloadAction<BroadcastPreviewContent & {isLMS?: boolean}>
        ) {
            state.fetchContentAPIStatus = APIStatus.Success
            const {breaktimes, timeOffset, breaktimeStyle, content, isLMS} = action.payload

            let lmsContent: Array<any> = []

            if (content.find((item) => item.id === EMPTY_CONTENT_ID) && !isLMS) {
                lmsContent = [...content]
            } else {
                if (content.find((item) => item.id !== EMPTY_CONTENT_ID)) {
                    lmsContent = [...(state.content?.content || []), ...content]
                }
            }

            state.content = {
                ...action.payload,
                breaktimes:
                    breaktimes && breaktimes.length > 0 && timeOffset
                        ? breaktimes.map((it) => ({
                              start: it.start + timeOffset,
                              end: it.end + timeOffset,
                              style: breaktimeStyle,
                          }))
                        : breaktimes,

                content: state.content?.content?.length && isLMS ? lmsContent : content,
            }
            state.needUpdateState = true
        },
        setNeedUpdateState(state: SliceState, action: PayloadAction<{needUpdateState: boolean}>) {
            const {needUpdateState} = action.payload

            state.needUpdateState = needUpdateState

            return state
        },
        setNeedReFetch(state: SliceState, action: PayloadAction<boolean>) {
            state.needReFetch = action.payload
        },

        startAlertFetch(state: SliceState) {
            state.fetchAlertAPIStatus = APIStatus.Loading
        },
        failAlertFetch(state: SliceState) {
            return {
                ...state,
                fetchAlertAPIStatus: APIStatus.Failure,
                alert: undefined,
            }
        },
        successAlertFetch(
            state: SliceState,
            action: PayloadAction<{broadcastPreviewAlert: BroadcastPreviewAlert}>
        ) {
            const {broadcastPreviewAlert} = action.payload

            return {
                ...state,
                fetchAlertAPIStatus: APIStatus.Success,
                alert: broadcastPreviewAlert,
            }
        },

        resetContent(state: SliceState) {
            if (state.content) {
                state.content.content = []
            }
        },

        reset() {
            return initialState
        },
    },
})

export interface BroadcastPreviewStore {
    broadcastPreview: SliceState
}

export const broadcastPreviewSelectors = {
    getFetchContentAPIStatus: (store: BroadcastPreviewStore) =>
        store.broadcastPreview.fetchContentAPIStatus,
    getContent: (store: BroadcastPreviewStore) => store.broadcastPreview.content,
    getContentList: (store: BroadcastPreviewStore) => store.broadcastPreview.content?.content,
    getLastContentItem: (store: BroadcastPreviewStore) =>
        store.broadcastPreview.content?.content?.length
            ? store.broadcastPreview.content.content[
                  store.broadcastPreview.content.content.length - 1
              ]
            : undefined,
    getNeedUpdateState: (store: BroadcastPreviewStore) => store.broadcastPreview.needUpdateState,
    getNeedReFetch: (store: BroadcastPreviewStore) => store.broadcastPreview.needReFetch,

    getFetchAlertAPIStatus: (store: BroadcastPreviewStore) =>
        store.broadcastPreview.fetchAlertAPIStatus,
    getAlert: (store: BroadcastPreviewStore) => store.broadcastPreview.alert,
}
